import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import AppsIcon from "@material-ui/icons/Apps";
import BusinessIcon from "@material-ui/icons/Business";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import CloseIcon from "@material-ui/icons/Close";
import EventIcon from "@material-ui/icons/Event";
import GroupIcon from "@material-ui/icons/Group";
import HowToRegIcon from "@material-ui/icons/HowToReg";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import PlaceIcon from "@material-ui/icons/Place";
import SchoolOutlinedIcon from "@material-ui/icons/SchoolOutlined";
import {
  deleteCongresoDelegado,
  getCongresoDelegadosByEdicion,
  lookupCongresoDelegadoEmail,
  updateCongresoDelegadoAcl,
  upsertCongresoDelegado,
} from "../../../../api/congresoEdicion";
import { alertError, alertSuccess } from "../../../../utils/logger";
import {
  ACCESS_EDITION_ADMIN,
  ACCESS_READ,
  ACCESS_SECTIONS,
  CONGRESO_SECTION_OPTIONS,
} from "./congresoShareConstants";

function normalizeAclRow(row) {
  if (!row) return row;
  if (row.accessLevel) {
    return {
      ...row,
      sectionKeys: Array.isArray(row.sectionKeys) ? row.sectionKeys : [],
    };
  }
  if (row.canEdit === false) {
    return { ...row, accessLevel: ACCESS_READ, sectionKeys: [] };
  }
  return {
    ...row,
    accessLevel: ACCESS_EDITION_ADMIN,
    sectionKeys: CONGRESO_SECTION_OPTIONS.map((o) => o.key),
  };
}

function initials(u) {
  const n = (u && (u.fullName || u.email || "")).trim();
  if (!n) return "?";
  const p = n.split(/[\s@]+/).filter(Boolean);
  if (p.length >= 2) return (p[0][0] + p[1][0]).toUpperCase();
  return n.slice(0, 2).toUpperCase();
}

const SECTION_OPTION_ICONS = {
  general: AppsIcon,
  carta: MailOutlineIcon,
  comites: GroupIcon,
  programa: EventIcon,
  talleres: SchoolOutlinedIcon,
  comunicacion: ChatBubbleOutlineIcon,
  sede: PlaceIcon,
  inscripcion: HowToRegIcon,
  patrocinadores: BusinessIcon,
};

/** Comparación barata para React.memo en filas de delegado */
function sectionKeysSignature(keys) {
  if (!keys || !keys.length) return "";
  const a = [...keys];
  a.sort();
  return a.join(",");
}

const SectionToggleCards = React.memo(function SectionToggleCards({
  selectedKeys,
  onToggleKey,
  disabled,
  ariaLabel,
}) {
  const selectedSet = useMemo(
    () => new Set(selectedKeys || []),
    [selectedKeys]
  );

  return (
    <div
      className="congreso-share-section-cards"
      role="group"
      aria-label={ariaLabel || "Apartados editables"}
    >
      {CONGRESO_SECTION_OPTIONS.map((o) => {
        const on = selectedSet.has(o.key);
        const Icon = SECTION_OPTION_ICONS[o.key] || AppsIcon;
        return (
          <button
            key={o.key}
            type="button"
            className={`congreso-share-section-card${
              on ? " congreso-share-section-card--on" : " congreso-share-section-card--off"
            }`}
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onToggleKey(o.key)}
          >
            <span className="congreso-share-section-card__icon" aria-hidden>
              <Icon />
            </span>
            <span className="congreso-share-section-card__label">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
});

const CongresoShareDelegadoCard = React.memo(
  function CongresoShareDelegadoCard({
    row,
    rowAcl,
    accessValue,
    effectiveSectionKeys,
    onAccessChange,
    onToggleSection,
    onRemove,
  }) {
    const u = row.user || {};
    const label = u.fullName || u.email || "Usuario";
    const secondary = u.email && u.fullName ? u.email : null;

    const handleSectionKey = useCallback(
      (key) => {
        onToggleSection(row._id, key);
      },
      [row._id, onToggleSection]
    );

    return (
      <Box
        component="article"
        className="congreso-share-delegado-card"
        aria-label={`Acceso de ${label}`}
      >
        <div className="congreso-share-delegado-card__header">
          <Avatar className="congreso-share-delegado-card__avatar">{initials(u)}</Avatar>
          <div className="congreso-share-delegado-card__header-text">
            <Typography variant="subtitle1" component="div" noWrap>
              {label}
            </Typography>
            {secondary ? (
              <Typography variant="body2" color="textSecondary" component="div" noWrap>
                {secondary}
              </Typography>
            ) : null}
          </div>
          <IconButton
            size="small"
            className="congreso-share-delegado-card__remove"
            onClick={() => onRemove(row._id)}
            aria-label="Quitar acceso"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className="congreso-share-delegado-card__permisos">
          <Typography
            variant="caption"
            component="div"
            className="congreso-share-delegado-card__block-title"
          >
            Nivel de acceso
          </Typography>
          <RadioGroup
            row
            aria-label={`Permiso de ${label}`}
            name={`access-${row._id}`}
            value={accessValue}
            onChange={(e) => {
              void onAccessChange(row, e.target.value);
            }}
            className="congreso-share-delegado-card__radio-group"
          >
            <FormControlLabel
              value={ACCESS_READ}
              control={<Radio size="small" color="primary" />}
              label="Solo lectura"
            />
            <FormControlLabel
              value={ACCESS_SECTIONS}
              control={<Radio size="small" color="primary" />}
              label="Apartados"
            />
            <FormControlLabel
              value={ACCESS_EDITION_ADMIN}
              control={<Radio size="small" color="primary" />}
              label="Admin de la edición"
            />
          </RadioGroup>
        </div>

        {rowAcl.accessLevel === ACCESS_SECTIONS && (
          <div className="congreso-share-delegado-card__apartados">
            <Typography
              variant="caption"
              component="div"
              className="congreso-share-delegado-card__block-title"
            >
              Apartados editables
            </Typography>
            <SectionToggleCards
              selectedKeys={effectiveSectionKeys}
              onToggleKey={handleSectionKey}
              ariaLabel={`Apartados editables de ${label}`}
            />
            <Typography
              variant="caption"
              color="textSecondary"
              component="p"
              className="congreso-share-delegado-card__hint"
            >
              Los cambios se guardan solos al marcar o desmarcar.
            </Typography>
          </div>
        )}
      </Box>
    );
  },
  (prev, next) =>
    prev.row._id === next.row._id &&
    prev.accessValue === next.accessValue &&
    prev.rowAcl.accessLevel === next.rowAcl.accessLevel &&
    sectionKeysSignature(prev.effectiveSectionKeys) ===
      sectionKeysSignature(next.effectiveSectionKeys) &&
    (prev.row.user && prev.row.user.email) === (next.row.user && next.row.user.email) &&
    (prev.row.user && prev.row.user.fullName) ===
      (next.row.user && next.row.user.fullName) &&
    prev.onAccessChange === next.onAccessChange &&
    prev.onToggleSection === next.onToggleSection &&
    prev.onRemove === next.onRemove
);

export default function CongresoShareDialog({ open, onClose, edicionId }) {
  const [rows, setRows] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteAccess, setInviteAccess] = useState(ACCESS_EDITION_ADMIN);
  const [inviteSectionKeys, setInviteSectionKeys] = useState(
    () => CONGRESO_SECTION_OPTIONS.map((o) => o.key)
  );
  const [newFullName, setNewFullName] = useState("");
  const [lookupHint, setLookupHint] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sectionsByAcl, setSectionsByAcl] = useState({});
  /** Hubo cambios persistidos en la lista o permisos desde que se abrió el diálogo (para mostrar “Listo”) */
  const [shareDirty, setShareDirty] = useState(false);

  const sectionsByAclRef = useRef(sectionsByAcl);
  sectionsByAclRef.current = sectionsByAcl;
  const sectionPersistTimersRef = useRef({});

  const allSectionKeys = useMemo(
    () => CONGRESO_SECTION_OPTIONS.map((o) => o.key),
    []
  );

  const load = useCallback(async () => {
    if (!edicionId || !open) return;
    setLoadingList(true);
    try {
      const res = await getCongresoDelegadosByEdicion(edicionId);
      if (res.status === 200) {
        const raw = res.data || [];
        const next = raw.map(normalizeAclRow);
        setRows(next);
        const map = {};
        next.forEach((r) => {
          if (r.accessLevel === ACCESS_SECTIONS) {
            map[r._id] =
              r.sectionKeys && r.sectionKeys.length
                ? [...r.sectionKeys]
                : [...allSectionKeys];
          }
        });
        setSectionsByAcl(map);
      }
    } catch (e) {
      alertError({
        error: e,
        customMessage: "No se pudo cargar la lista de personas con acceso.",
      });
    } finally {
      setLoadingList(false);
    }
  }, [edicionId, open, allSectionKeys]);

  useEffect(() => {
    if (!open) {
      setInviteEmail("");
      setLookupHint(null);
      setNewFullName("");
      setInviteAccess(ACCESS_EDITION_ADMIN);
      setInviteSectionKeys([...allSectionKeys]);
      setShareDirty(false);
      return;
    }
    setShareDirty(false);
    load();
  }, [open, load, allSectionKeys]);

  const runLookup = useCallback(async (emailTrim) => {
    if (!emailTrim || !emailTrim.includes("@")) {
      setLookupHint(null);
      return;
    }
    try {
      const res = await lookupCongresoDelegadoEmail(emailTrim);
      if (res.status !== 200) return;
      const { status } = res.data;
      if (status === "missing") {
        setLookupHint("missing");
      } else {
        setLookupHint("ok");
      }
    } catch {
      setLookupHint(null);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const em = inviteEmail.trim().toLowerCase();
    if (!em.includes("@")) return undefined;
    const t = setTimeout(() => {
      runLookup(em);
    }, 450);
    return () => clearTimeout(t);
  }, [inviteEmail, open, runLookup]);

  const clearSectionTimer = (aclId) => {
    const prev = sectionPersistTimersRef.current[aclId];
    if (prev) clearTimeout(prev);
    delete sectionPersistTimersRef.current[aclId];
  };

  const persistRowSectionsDebounced = useCallback((aclId) => {
    clearSectionTimer(aclId);
    sectionPersistTimersRef.current[aclId] = setTimeout(async () => {
      const keys = sectionsByAclRef.current[aclId] || [];
      if (!keys.length) {
        return;
      }
      try {
        const res = await updateCongresoDelegadoAcl(aclId, {
          accessLevel: ACCESS_SECTIONS,
          sectionKeys: keys,
        });
        if (res.status === 200) {
          setShareDirty(true);
          const doc = res.data || {};
          const nextKeys = Array.isArray(doc.sectionKeys) ? doc.sectionKeys : keys;
          setSectionsByAcl((prev) => ({ ...prev, [aclId]: [...nextKeys] }));
          setRows((prev) =>
            prev.map((r) => {
              if (r._id !== aclId) return r;
              return normalizeAclRow({
                ...r,
                accessLevel: doc.accessLevel || ACCESS_SECTIONS,
                sectionKeys: nextKeys,
                ...(typeof doc.canEdit === "boolean" ? { canEdit: doc.canEdit } : {}),
              });
            })
          );
        }
      } catch (e) {
        alertError({
          error: e,
          customMessage: e?.response?.data?.message || "No se pudo guardar los apartados.",
        });
      } finally {
        delete sectionPersistTimersRef.current[aclId];
      }
    }, 500);
  }, []);

  const handleToggleRowSection = useCallback(
    (aclId, key) => {
      setSectionsByAcl((prev) => {
        const cur = new Set(prev[aclId] || allSectionKeys);
        if (cur.has(key)) cur.delete(key);
        else cur.add(key);
        return { ...prev, [aclId]: Array.from(cur) };
      });
      persistRowSectionsDebounced(aclId);
    },
    [allSectionKeys, persistRowSectionsDebounced]
  );

  const handleInviteSectionToggle = useCallback((key) => {
    setInviteSectionKeys((prev) => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key);
      else s.add(key);
      return Array.from(s);
    });
  }, []);

  const inviteReadyToAdd = useMemo(() => {
    const email = inviteEmail.trim();
    if (!email || !email.includes("@")) return false;
    if (!lookupHint) return false;
    if (lookupHint !== "ok" && lookupHint !== "missing") return false;
    if (inviteAccess === ACCESS_SECTIONS && inviteSectionKeys.length === 0) return false;
    if (lookupHint === "missing") {
      if (!newFullName.trim()) return false;
    }
    return true;
  }, [
    inviteEmail,
    lookupHint,
    inviteAccess,
    inviteSectionKeys,
    newFullName,
  ]);

  const handleInviteAdd = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      alertError({ customMessage: "Indica un correo electrónico válido." });
      return;
    }

    let hint = lookupHint;
    try {
      const lk = await lookupCongresoDelegadoEmail(email);
      if (lk.status === 200) {
        const d = lk.data;
        if (d.status === "missing") hint = "missing";
        else hint = "ok";
        setLookupHint(hint);
      }
    } catch {
      alertError({ customMessage: "No se pudo comprobar el correo." });
      return;
    }

    if (inviteAccess === ACCESS_SECTIONS && !inviteSectionKeys.length) {
      alertError({
        customMessage: "Selecciona al menos un apartado editable.",
      });
      return;
    }
    if (hint === "missing") {
      if (!newFullName.trim()) {
        alertError({ customMessage: "Indica el nombre completo de la persona invitada." });
        return;
      }
    }

    const body = {
      edicionId,
      email,
      accessLevel: inviteAccess,
      sectionKeys:
        inviteAccess === ACCESS_SECTIONS ? inviteSectionKeys : undefined,
    };
    if (hint === "missing") {
      body.newUser = {
        fullName: newFullName.trim(),
      };
    }

    setSaving(true);
    try {
      const res = await upsertCongresoDelegado(body);
      if (res.status === 200) {
        const d = res.data || {};
        const sent = d.inviteEmailSent === true;
        const failed = d.inviteEmailSent === false && d.inviteEmailError;
        alertSuccess({
          title: "Añadido",
          customMessage: sent
            ? "Acceso creado. Hemos enviado un correo con un enlace único para que la persona defina su contraseña."
            : failed
              ? "Acceso creado, pero no se pudo enviar el correo de invitación. Revisa el servidor de correo o vuelve a intentarlo."
              : "Acceso actualizado correctamente.",
        });
        setInviteEmail("");
        setLookupHint(null);
        setNewFullName("");
        setInviteAccess(ACCESS_EDITION_ADMIN);
        setInviteSectionKeys([...allSectionKeys]);
        setShareDirty(true);
        load();
      }
    } catch (e) {
      alertError({
        error: e,
        customMessage:
          e?.response?.data?.message || "No se pudo añadir o actualizar el acceso.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRowAccessChange = useCallback(
    async (row, nextLevel) => {
      clearSectionTimer(row._id);
      const prevSections = sectionsByAclRef.current[row._id];
      let keys =
        nextLevel === ACCESS_SECTIONS
          ? prevSections && prevSections.length
            ? [...prevSections]
            : [...allSectionKeys]
          : [];
      if (nextLevel === ACCESS_SECTIONS) {
        setSectionsByAcl((p) => ({ ...p, [row._id]: keys }));
      }
      try {
        const res = await updateCongresoDelegadoAcl(row._id, {
          accessLevel: nextLevel,
          sectionKeys: nextLevel === ACCESS_SECTIONS ? keys : [],
        });
        if (res.status === 200) {
          setShareDirty(true);
          load();
        }
      } catch (e) {
        alertError({
          error: e,
          customMessage: e?.response?.data?.message || "No se pudo actualizar el rol.",
        });
      }
    },
    [allSectionKeys, load]
  );

  const handleRemove = useCallback((aclId) => {
    deleteCongresoDelegado(aclId)
      .then((res) => {
        if (res.status === 204) {
          alertSuccess({ title: "Hecho", customMessage: "Acceso eliminado." });
          setShareDirty(true);
          load();
        }
      })
      .catch((e) => {
        alertError({ error: e, customMessage: "No se pudo quitar el acceso." });
      });
  }, [load]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="body"
      aria-labelledby="congreso-share-title"
      PaperProps={{
        style: { overflow: "visible" },
      }}
    >
      <DialogTitle id="congreso-share-title" style={{ paddingBottom: 8 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="span">
            Compartir esta edición
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers style={{ paddingTop: 16 }}>
        <Typography variant="body2" color="textSecondary" paragraph>
          Indica el correo y el permiso. Si la persona no tiene cuenta, añade su nombre completo: recibirá un
          correo con un enlace único para que elija su contraseña (nadie en ACTEDI la conoce). Si ya tiene cuenta,
          se le añade acceso a esta edición con el correo existente.
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          margin="normal"
          label="Correo electrónico"
          placeholder="ejemplo@organización.org"
          value={inviteEmail}
          onChange={(e) => {
            const v = e.target.value;
            setInviteEmail(v);
            setLookupHint(null);
          }}
          onBlur={() => runLookup(inviteEmail.trim().toLowerCase())}
        />

        {lookupHint === "missing" && (
          <Box mt={1} mb={2} p={2} bgcolor="grey.50" borderRadius={4}>
            <Typography variant="subtitle2" gutterBottom>
              Nuevo delegado
            </Typography>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label="Nombre completo"
              value={newFullName}
              onChange={(e) => setNewFullName(e.target.value)}
            />
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 12 }}>
              Se enviará un correo a la dirección indicada con un enlace seguro (válido varios días) para que
              defina su contraseña y active el acceso al backoffice de congresos.
            </Typography>
          </Box>
        )}

        <FormControl component="fieldset" margin="normal" fullWidth>
          <Typography component="legend" variant="subtitle2" style={{ marginBottom: 8 }}>
            Permiso
          </Typography>
          <RadioGroup
            aria-label="Permiso de invitación"
            name="invite-access"
            value={inviteAccess}
            onChange={(e) => setInviteAccess(e.target.value)}
          >
            <FormControlLabel
              value={ACCESS_READ}
              control={<Radio color="primary" />}
              label="Solo lectura"
            />
            <FormControlLabel
              value={ACCESS_SECTIONS}
              control={<Radio color="primary" />}
              label="Apartados (seleccionar)"
            />
            <FormControlLabel
              value={ACCESS_EDITION_ADMIN}
              control={<Radio color="primary" />}
              label="Administrador de esta edición"
            />
          </RadioGroup>
        </FormControl>

        {inviteAccess === ACCESS_SECTIONS && (
          <Box mt={1.5}>
            <Typography
              variant="caption"
              component="div"
              style={{
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                color: "#7e8299",
                marginBottom: 8,
              }}
            >
              Apartados editables
            </Typography>
            <SectionToggleCards
              selectedKeys={inviteSectionKeys}
              onToggleKey={handleInviteSectionToggle}
              disabled={saving}
              ariaLabel="Apartados editables del invitado"
            />
          </Box>
        )}

        <Box mt={2} mb={1}>
          <Button
            fullWidth
            variant={inviteReadyToAdd ? "contained" : "outlined"}
            size={inviteReadyToAdd ? "large" : "medium"}
            disabled={saving || !inviteReadyToAdd}
            onClick={handleInviteAdd}
            style={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 600,
              minHeight: inviteReadyToAdd ? 52 : 40,
              borderColor: "#f3691a",
              color: inviteReadyToAdd ? "#fff" : "#f3691a",
              backgroundColor: inviteReadyToAdd ? "#f3691a" : "transparent",
              boxShadow: inviteReadyToAdd
                ? "0 4px 14px rgba(243, 105, 26, 0.45)"
                : "none",
            }}
          >
            Añadir
          </Button>
          {!inviteReadyToAdd &&
            (lookupHint === "ok" || lookupHint === "missing") &&
            inviteEmail.trim().includes("@") && (
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
              Completa los datos que falten (contraseña y nombre si es usuario nuevo) para habilitar el botón.
            </Typography>
          )}
        </Box>

        <Divider style={{ margin: "20px 0" }} />

        <Typography variant="subtitle1" gutterBottom>
          Personas con acceso
        </Typography>
        {loadingList ? (
          <Typography variant="body2" color="textSecondary">
            Cargando…
          </Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            Solo el personal interno tiene acceso por ahora.
          </Typography>
        ) : (
          <Box className="congreso-share-delegado-list">
            {rows.map((row) => {
              const rowAcl = normalizeAclRow(row);
              const accessValue =
                rowAcl.accessLevel === ACCESS_READ ||
                rowAcl.accessLevel === ACCESS_SECTIONS ||
                rowAcl.accessLevel === ACCESS_EDITION_ADMIN
                  ? rowAcl.accessLevel
                  : ACCESS_EDITION_ADMIN;
              const effectiveSectionKeys =
                sectionsByAcl[row._id] !== undefined
                  ? sectionsByAcl[row._id]
                  : rowAcl.sectionKeys && rowAcl.sectionKeys.length
                    ? [...rowAcl.sectionKeys]
                    : [...allSectionKeys];
              return (
                <CongresoShareDelegadoCard
                  key={row._id}
                  row={row}
                  rowAcl={rowAcl}
                  accessValue={accessValue}
                  effectiveSectionKeys={effectiveSectionKeys}
                  onAccessChange={handleRowAccessChange}
                  onToggleSection={handleToggleRowSection}
                  onRemove={handleRemove}
                />
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        {shareDirty && (
          <Button
            onClick={onClose}
            variant="contained"
            style={{
              backgroundColor: "#f3691a",
              color: "#fff",
              borderRadius: 999,
              textTransform: "none",
              minWidth: 88,
              boxShadow: "0 2px 8px rgba(243, 105, 26, 0.4)",
            }}
          >
            Listo
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
